import { Users } from 'lucide-react';
import { Button } from '@vnetwork/ui/primitives';
import { EmptyState } from '@vnetwork/ui/data';
import { useIamUsers, type GroupMember } from '@/api/s3';
import { Mono } from '../../s3-parts';
import { AttachPicker } from '../AttachPicker';

/**
 * Group membership editor — lists current members (remove per row) and a picker
 * of the service's users not already in the group. Parent owns the mutations
 * (bound to the group name) and passes `onAdd`/`onRemove`.
 */
export function GroupMembers({
  serviceId,
  members,
  onAdd,
  onRemove,
  pending,
}: {
  serviceId: string;
  members: GroupMember[];
  onAdd: (userName: string) => void;
  onRemove: (userName: string) => void;
  pending?: boolean;
}) {
  const { data: users = [] } = useIamUsers(serviceId);
  const memberNames = new Set(members.map((m) => m.user_name));
  const options = users
    .filter((u) => !memberNames.has(u.user_name))
    .map((u) => ({ value: u.user_name, label: u.user_name }));

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <AttachPicker
        options={options}
        onAdd={onAdd}
        pending={pending}
        placeholder="Select a user…"
        addLabel="Add user"
        emptyHint="All users are already members, or none exist yet."
      />

      {members.length === 0 ? (
        <EmptyState
          icon={<Users size={20} />}
          title="No members yet"
          description="Add a user to include them in this group."
        />
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 'var(--vn-space-2)' }}>
          {members.map((m) => (
            <li
              key={m.user_name}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 'var(--vn-space-3)',
                border: '1px solid var(--vn-border-subtle)',
                borderRadius: 'var(--vn-radius-md)',
                padding: 'var(--vn-space-2) var(--vn-space-3)',
              }}
            >
              <Mono>{m.user_name}</Mono>
              <Button variant="ghost" size="sm" disabled={pending} onClick={() => onRemove(m.user_name)}>
                Remove
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
